// ADR 6.9.26p §2/§3 — three events over 6.9.26c's `capture()`, and ADR 9.9.26b
// §1's fourth, which is the route table's rather than the shell's.
//
// They are the input to `adaptive-layout.toml`'s web panels: the one client
// whose window width is a user's *choice* rather than a device fact.
//
// The `web_` naming rule and the injected sink live in `layoutSink.ts`
// (7.10.26v §6), re-exported here so no importer changed.

import { launchLocale } from '../i18n/localeStore'
import { reportNonFatal } from '../telemetry/capture'
import type { TelemetryConsent } from '../telemetry/consent'
import type { Scheme } from '../ui/theme'

import type { SpreadLayout, SpreadPreference } from '../wasm'
import { LAYOUT_EVENTS, sendLayoutEvent as sink } from './layoutSink'
import type { WindowClass, WindowSizeClass } from './useWindowClass'

export { LAYOUT_EVENTS, resetLayoutSink, setLayoutSink, type LayoutSink } from './layoutSink'

/**
 * The ceiling on §3's crossing counter.
 *
 * A drag across 320→1600px already costs at most two events — the hook debounces
 * an animation frame and returns its previous object when no answer moved — but
 * deliberate oscillation across 840 is unbounded. The 20th crossing IS emitted,
 * carrying `capped: true`; the 21st is not.
 */
export const MAX_CROSSINGS_PER_TAB = 20

// Module scope, not a `useRef`: React StrictMode double-invokes effects in
// development and the shell remounts on any route tree change, so a
// per-component guard would make "once per tab" true in production and false in
// the suite (AC2's shape). `previousClass` lives here for the same reason: a
// shell remount must not silently drop a crossing by forgetting the window.
let launched = false
let launchScheme: Scheme | null = null
let wasmInitMs: number | null = null
let staticShell: StaticShellOutcome | null = null
let pendingLaunch: Record<string, unknown> | null = null
let spreadLayout: SpreadLayout | null = null
let spreadPreference: SpreadPreference | null = null
let previousClass: WindowSizeClass | null = null
let crossings = 0

/**
 * Test-only: clears every piece of launch and crossing state. Vitest resets
 * modules per *file*, not per *test*, so without this AC1 and AC2 read each
 * other's leftovers and AC5's twenty crossings poison every case after it. The
 * house shape: 6.9.26b's `resetWindowAnalyticsState()` on the native side.
 */
export function resetLayoutAnalyticsState(): void {
  launched = false
  launchScheme = null
  // ADR 11.9.26a §2 — load-bearing: the real wasm module pushes a duration at
  // import time, and the whole-bag launch assertions need it cleared.
  wasmInitMs = null
  staticShell = null
  pendingLaunch = null
  spreadLayout = null
  spreadPreference = null
  previousClass = null
  crossings = 0
}

/** ADR 9.9.26j §1 — `main.tsx` hands over what `initTheme()` resolved. */
export function setLaunchScheme(scheme: Scheme): void {
  launchScheme = scheme
}

/** ADR 11.9.26a §2 — both init phases, whole ms. Pushed: a getter would import the TLA module. */
export function setWasmInitMs(ms: number): void {
  wasmInitMs = ms
}

/** ADR 23.9.26c §1 — what `StaticShellCommit` found; `stuck` may overwrite a frame later. */
export type StaticShellOutcome = 'removed' | 'absent' | 'stuck'

/** ADR 23.9.26c §1 — pushed by the marker's layout effect, then by its `rAF`. */
export function setStaticShell(outcome: StaticShellOutcome): void {
  staticShell = outcome
}

/**
 * ADR 7.10.26v §6 — pushed by the shell from an effect keyed on the spread,
 * declared before the observation effect so the launch sees it. `layout` is
 * `null` until the facade has answered: absent from the payload, never guessed.
 */
export function setLayoutSpread(layout: SpreadLayout | null, preference: SpreadPreference): void {
  spreadLayout = layout
  spreadPreference = preference
}

/** The spread's two properties as known now — the native clients' property names. */
function spreadProperties(): Record<string, unknown> {
  const properties: Record<string, unknown> = {}
  if (spreadPreference !== null) properties.spread_preference = spreadPreference
  if (spreadLayout !== null) properties.spread_layout = spreadLayout
  return properties
}

/**
 * Try the launch, and latch only if it landed. A retry carries the *current*
 * reading, deliberately: an undelivered launch recorded nothing, so the honest
 * subject of the retry is the window as it is now.
 *
 * ADR 23.9.26c §1 amends "transmits what it was handed": `static_shell` alone
 * is read here, at transmit time, because its `stuck` value first exists a
 * frame after the payload was built, and a retry replays that frozen object.
 * ADR 7.10.26v §6 — the spread is read here too: the facade answers after the
 * first paint, so a launch retried once it has carries `spread_layout`.
 */
function deliverLaunch(built: Record<string, unknown>): void {
  const shell = staticShell === null ? built : { ...built, static_shell: staticShell }
  const properties = { ...shell, ...spreadProperties() }
  if (sink(LAYOUT_EVENTS.launch, properties)) {
    launched = true
    pendingLaunch = null
    return
  }
  pendingLaunch = built
}

/**
 * The second half of the delivery latch, called from `main.tsx` when
 * `initTelemetry` settles — not a buffer inside `capture()`, which would change
 * the seam for every other event (6.9.26c's decision). A tab that denied consent
 * settles with nothing attached, so this delivers nothing and the launch stays pending.
 */
export function flushLaunchEvent(): void {
  if (launched || pendingLaunch === null) return
  deliverLaunch(pendingLaunch)
}

/**
 * Is a launch still undelivered? ADR 8.9.26b G3: `flushLaunchEvent()` is the only
 * retry, so a survivor is lost for the tab — expected where consent was denied,
 * a defect where it was granted, and from outside the two look identical.
 */
export function launchPending(): boolean {
  return !launched && pendingLaunch !== null
}

/**
 * Reports a launch that survived the flush, iff analytics consent was granted.
 * A helper rather than a body in `main.tsx`, because no suite can import
 * `main.tsx` (6.9.26p AC11).
 *
 * Sentry rather than PostHog (ADR 8.9.26b §3): the report is about PostHog not
 * being there. So a visitor who granted analytics but denied crash reports
 * leaves no trace here; the alternative was a second door through the gate.
 */
export function reportUndeliveredLaunch(consent: TelemetryConsent): void {
  if (consent.analytics_opt_out || !launchPending()) return
  reportNonFatal('web launch event never delivered', ['web-launch-never-delivered'])
}

/**
 * Emit the crossing, if this observation is one.
 *
 * **A new object from the hook is not a size-class crossing**, and this is the
 * one place an implementer gets the event wrong. `useWindowClass`'s `same()`
 * compares all three answers, so a height-only change across
 * `list_detail_min_height` at a fixed width hands back a new object with the
 * class pinned — 1000×500 → 1000×400 flips the pane layout and leaves the class
 * `expanded`. Emitting there would assert a change that did not happen, the
 * construct 6.9.26b §2 forbids. So this compares `sizeClass` and only that.
 *
 * The pane-layout flip therefore goes unrecorded, matching 6.9.26b's silence on
 * the phones. If the height guard turns out to be denying splits in practice
 * that is a new event with its own panel, not a loosened comparison here.
 */
function emitCrossing(from: WindowSizeClass, windowClass: WindowClass): void {
  if (crossings >= MAX_CROSSINGS_PER_TAB) return
  crossings += 1
  const properties: Record<string, unknown> = {
    from_class: from,
    to_class: windowClass.sizeClass,
    pane_layout: windowClass.paneLayout,
  }
  // ADR 7.10.26v §6 — opening a foldable is a compact → medium crossing.
  if (spreadLayout !== null) properties.spread_layout = spreadLayout
  // `capped` appears on that one event and on no other: a property PostHog has
  // seen can never be retired, so the narrower shape is the cheaper permanent
  // commitment, and no panel breaks down on it — nineteen `capped: false` rows
  // would be nineteen copies of a value nothing can read.
  if (crossings === MAX_CROSSINGS_PER_TAB) properties.capped = true
  sink(LAYOUT_EVENTS.classChanged, properties)
}

/**
 * One observation of the window class: the launch, then the crossing.
 *
 * Called from the shell's single effect, keyed on the object the hook returns —
 * referentially stable until an answer changes, so the effect runs exactly at
 * the moments worth reporting.
 *
 * **The first measurement seeds `previousClass` and emits no crossing.** That
 * observation is the launch; a crossing needs two by construction, so there is
 * no mount-time crossing to suppress and no fabricated `from_class`. Contrast
 * 6.9.26b §3, where the native launch read and the first composition read are a
 * frame apart and *can* straddle a boundary; the web has one read site.
 */
export function emitWindowObservation(windowClass: WindowClass): void {
  const previous = previousClass
  previousClass = windowClass.sizeClass

  if (!launched) {
    const properties: Record<string, unknown> = {
      window_size_class: windowClass.sizeClass,
      pane_layout: windowClass.paneLayout,
    }
    // ADRs 9.9.26j §1, 11.9.26a §2, 30.9.26d §7 — properties on the event every
    // session sends (never a second launch event splitting the population), each
    // absent, never `undefined` (a null breakdown band) and never guessed.
    if (launchScheme !== null) properties.colour_scheme = launchScheme
    if (wasmInitMs !== null) properties.wasm_init_ms = wasmInitMs
    const locale = launchLocale()
    if (locale !== null) Object.assign(properties, { ui_locale: locale.locale, ui_locale_source: locale.source })
    deliverLaunch(properties)
  }

  if (previous === null || previous === windowClass.sizeClass) return
  emitCrossing(previous, windowClass)
}

/**
 * §4 — the browser's half of 6.9.26i's question.
 *
 * Once per mount, per plan id, and both halves are load-bearing. *Per plan id*:
 * a re-render at an unchanged id emits nothing, so the count is opens rather
 * than renders. *Per mount*, not per tab: navigating away and returning is a
 * second open, and a global latch would under-count the browsing pattern the
 * two-pane layout exists to make cheap.
 *
 * The layout is read at emit time, so a plan opened single-pane and dragged into
 * the split stays single-pane — §5's ratio divides by the launch's layout, and a
 * numerator upgraded on a later resize would count a different moment. No
 * `is_demo`: no panel breaks down on it, and 6.9.26b §2's append-only argument
 * makes an unread property a permanent one.
 */
export function emitPlanViewed(planId: string, windowClass: WindowClass): void {
  sink(LAYOUT_EVENTS.planViewed, {
    plan_id: planId,
    pane_layout: windowClass.paneLayout,
  })
}

/**
 * ADR 9.9.26b §1 — an unmatched URL, reported once.
 *
 * One property, and the *path only*. The query string is where a share token, a
 * `?next=` (attacker-supplied — `router.tsx`'s `safeNextPath`) or a reset token
 * would be, and none belongs in an analytics warehouse. The caller reads
 * `useLocation().pathname` (`search` and `hash` are separate fields), so the
 * exclusion is already structural there; the strip below makes it the
 * *emitter's* contract, so a future second call site inherits it.
 *
 * A token can still ride in the `pathname` — a mistyped `/s/<token>/x` reaches
 * the catch-all. 9.9.26b accepted that, rejecting *heuristic* high-entropy
 * scrubbing because it would make the by-path panel lie about which paths are
 * being hit. ADR 24.9.26h §6 amends it: PostHog's `before_send`
 * (`telemetry/redact.ts`) now rewrites exactly the segment after `/s/`, so the
 * panel sees `/s/:token/x` — the path shape it counts — and no token.
 *
 * No delivery latch, and the sink's boolean is deliberately ignored: this fires
 * long after `initTelemetry` has settled, so there is no launch race to lose.
 */
export function emitRouteNotFound(path: string): void {
  const cut = path.search(/[?#]/)
  sink(LAYOUT_EVENTS.notFound, { path: cut === -1 ? path : path.slice(0, cut) })
}
